import React, { useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import Dialog from '../../ui/Dialog';
import EmptyState from '../../ui/EmptyState';
import Skeleton from '../../ui/Skeleton';
import { sourceLogoExtension, sourceLogoSlug } from '../../api-utils/sources';
import { useSources } from '../../data/queries/hooks';
import { availableApps, type CatalogApp, type CatalogKind } from '../../lib/sourceCatalog';
import SourceLogo from './SourceLogo';

interface AvailableSourcesDialogProps {
  /** Tools whose alerts are read (Connected Sources), or feeds of threat intelligence (Threat Intel). */
  kind?: CatalogKind;
  onClose: () => void;
  /** The application that was picked, or null for one that is not listed. */
  onPick: (app: CatalogApp | null) => void;
}

/**
 * The menu behind "Connect New Source" and Threat Intel's "Connect a source" (client notes 10/26, items 12 and Threat Intel 2):
 * the applications that can still be connected, by name. Picking one goes on to the connect form with its name in.
 */
const AvailableSourcesDialog: React.FC<AvailableSourcesDialogProps> = ({ kind = 'tool', onClose, onPick }) => {
  const sources = useSources();
  const loading = sources.loading;
  const apps = useMemo(() => availableApps(sources.data ?? [], kind), [sources.data, kind]);
  const title = kind === 'intel' ? 'Connect a threat intel source' : 'Connect a new source';

  return (
    <Dialog title={title} size="sm" onClose={onClose}>
      <p className="text-sm text-c3-mute mb-3">Choose the application to connect. You set it up on the next screen.</p>
      {loading ? (
        <Skeleton lines={4} label="Loading the available sources…" />
      ) : apps.length === 0 ? (
        <EmptyState title="Everything is connected">
          Every application we can connect is already one of your sources.
        </EmptyState>
      ) : (
        <ul className="c3-avail-list" aria-label="Available sources">
          {apps.map((app) => {
            const slug = sourceLogoSlug({ provider: app.provider, name: app.name, candidate_source: app.provider });
            return (
              <li key={app.provider}>
                <button type="button" className="c3-avail-item" onClick={() => onPick(app)}>
                  <span className="c3-src-logo c3-avail-logo">
                    <SourceLogo slug={slug} ext={sourceLogoExtension(slug)} name={app.name} />
                  </span>
                  <span className="c3-avail-name">{app.name}</span>
                  <ChevronRight size={16} strokeWidth={1.5} aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <button type="button" className="c3-btn c3-btn-text c3-btn-sm mt-3" onClick={() => onPick(null)}>
        Connect a source that is not listed
      </button>
    </Dialog>
  );
};

export default AvailableSourcesDialog;
